import { useEffect, useMemo, useState } from 'react'
import { Button } from '@diwata/ironvale'
import { NeedsYou } from '@/components/NeedsYou'
import { DiwaAPI } from '@/api/client'
import { useUI } from '@/store/ui'

function b64ToUint8(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4)
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** Enroll this device: permission → pushManager.subscribe → server remembers. */
function EnableAlerts() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [state, setState] = useState<'unknown' | 'unavailable' | 'off' | 'on' | 'busy'>('unknown')

  useEffect(() => {
    ;(async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setState('unavailable')
        return
      }
      try {
        const cfg = await api.get<{ enabled: boolean }>('/push/config')
        if (!cfg.enabled) {
          setState('unavailable')
          return
        }
        const reg = await navigator.serviceWorker.getRegistration()
        const sub = await reg?.pushManager.getSubscription()
        setState(sub ? 'on' : 'off')
      } catch {
        setState('unavailable')
      }
    })()
  }, [api])

  async function enable() {
    setState('busy')
    try {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') {
        setState('off')
        return
      }
      const cfg = await api.get<{ public_key: string }>('/push/config')
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64ToUint8(cfg.public_key),
      })
      const json = sub.toJSON()
      await api.post('/push/subscribe', {
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      })
      setState('on')
    } catch {
      setState('off')
    }
  }

  if (state === 'unknown' || state === 'unavailable') return null
  if (state === 'on') return <span className="dw-inbox-detail">phone alerts on</span>
  return (
    <Button size="sm" variant="ghost" disabled={state === 'busy'} onClick={enable}>
      {state === 'busy' ? 'Enabling…' : '🔔 Enable phone alerts'}
    </Button>
  )
}

// The ◈ queue as a full page — on a phone this IS the front door: everything that
// needs your judgment, one thumb, one list. The rail tab remains on desktop.
export function NeedsYouPage() {
  return (
    <div className="dw-page-narrow">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">◈ Needs you</div>
          <div className="dw-page-subtitle">
            Gates, questions, and fresh signals — judgment happens here.
          </div>
        </div>
        <EnableAlerts />
      </div>
      <NeedsYou />
    </div>
  )
}
